import { useState, useEffect } from 'react';
import supabase from '../lib/supabase';

// Default fallback values — used if Supabase fetch fails or table not yet populated
export const DEFAULT_SETTINGS = {
  trial_days: 14,
  annual_price_display: '$9.99',
  annual_price_id: '',
  annual_enabled: true,
  monthly_price_display: '',
  monthly_price_id: '',
  monthly_enabled: false,
  // Color Mix Guide
  color_brands: ['Americolor', 'Chefmaster', 'Colour Mill', 'Food Network', 'Spectrum Flow', 'Sugarflair', 'Wilton', 'Natural'],
  color_mix_ai_prompt: '',
  color_mix_ai_model: 'claude-sonnet-4-20250514',
  color_mix_anon_limit: 10,
  color_mix_user_limit: 10,
  cookie_ai_review_anon_limit: 10,
  cookie_ai_review_user_limit: 30,
  cookie_ai_size_prompt: '',
  cookie_ai_colors_prompt: '',
  // Email confirmation on signup (mirrors Supabase Auth setting — must be changed there too)
  require_email_confirmation: true,
  // Default Sample Cookie (always stored in inches + grams)
  default_reference_cookie: {
    width: '2.5',
    length: '3.5',
    weightNoIcing: '40',
    weight: '54',
  },
};

let cachedSettings = null;
let cacheTimestamp = null;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export async function fetchAppSettings() {
  const now = Date.now();
  if (cachedSettings && cacheTimestamp && now - cacheTimestamp < CACHE_TTL_MS) {
    return cachedSettings;
  }

  const { data, error } = await supabase
    .from('app_settings')
    .select('key, value');

  if (error || !data) {
    return { ...DEFAULT_SETTINGS };
  }

  const settings = { ...DEFAULT_SETTINGS };
  data.forEach(({ key, value }) => {
    try {
      settings[key] = JSON.parse(value);
    } catch {
      settings[key] = value;
    }
  });

  cachedSettings = settings;
  cacheTimestamp = now;
  return settings;
}

export function invalidateSettingsCache() {
  cachedSettings = null;
  cacheTimestamp = null;
}

export function useAppSettings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppSettings().then((s) => {
      setSettings(s);
      setLoading(false);
    });
  }, []);

  return { settings, loading };
}
